import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useHistory } from 'react-router';
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import MissingSignaturesRecipients from '~/components/MissingSignaturesRecipients/MissingSignaturesRecipients';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { routes } from '~/constants';
import { useEventReporter, useStoreActions } from '~/contexts';
import { useDraftProposal } from '~/hooks/reactQuery/mutations/useDraftProposal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { useDuplicateProposal } from './reactQuery/mutations/useDuplicateProposal';
import { useCopyProposalLink } from './useCopyProposalLink';
import { useFeatureFlags } from './useFeatureFlags';
import { useSendReminder } from './useSendReminder';
import { useTerminateAgreement } from './useTerminateAgreement';
import { useWithdrawToEditProposal } from './useWithdrawToEditProposal';
const MissingSignaturesReminderModal = (param)=>{
    let { relationship, dismissModal } = param;
    const { t: texts } = useLocaleKeys('hooks.proposalActions');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { sendReminder } = useSendReminder();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        textAlignCenter: false,
        title: texts.sendReminder(),
        primaryButtonLabel: commonButtons.sendNow(),
        primaryButtonTestId: "send-propposal-approval-reminder-button",
        secondaryButtonLabel: commonButtons.back(),
        secondaryButtonTestId: "cancel-send-propposal-approval-reminder-button",
        onCloseButtonClick: dismissModal,
        onPrimaryButtonClick: ()=>{
            sendReminder(relationship);
            dismissModal();
        },
        onSecondaryButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs("div", {
            "data-testid": "send-proposal-approval-reminder-modal",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    children: texts.sendReminderDescription()
                }),
                /*#__PURE__*/ _jsx(MissingSignaturesRecipients, {
                    relationship: relationship
                })
            ]
        })
    });
};
export function useProposalActions() {
    const { t: texts } = useLocaleKeys('hooks.proposalActions');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { showModal } = useOverlayContext();
    const { api } = useStoreActions();
    const { withdrawToEditProposalByIdWithConfirmation } = useWithdrawToEditProposal();
    const { EDIT_PROPOSAL_IN_PLACE } = useFeatureFlags();
    const { reportToGtm, reportToAppcues } = useEventReporter();
    const { copyAgreementLink } = useCopyProposalLink();
    const { cancelProposalById } = useTerminateAgreement();
    const history = useHistory();
    const { draftProposal } = useDraftProposal({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId))
    });
    const { duplicateProposal } = useDuplicateProposal({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId)),
        withPageLoader: true
    });
    return {
        getEditProposalAction (proposal, origin) {
            const fromProposalDashboard = origin === 'proposalsDashboard';
            return {
                text: fromProposalDashboard ? texts.edit() : texts.editProposal(),
                intent: Intent.EditProposal,
                disabled: proposal.isSample,
                onClick: async ()=>{
                    if (EDIT_PROPOSAL_IN_PLACE || fromProposalDashboard) {
                        withdrawToEditProposalByIdWithConfirmation(proposal.id, proposal.draftId);
                    } else {
                        const confirmed = await showModal({
                            closeOnClickOutside: true,
                            closeOnEscapeKey: true,
                            content: (param)=>{
                                let { dismissModal } = param;
                                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                                    textAlignCenter: false,
                                    skin: "danger",
                                    title: texts.withdrawToEdit(),
                                    content: texts.withdrawToEditContent(),
                                    primaryButtonLabel: texts.withdrawAndEdit(),
                                    primaryButtonTestId: "confirm-withdraw-to-edit",
                                    secondaryButtonLabel: commonButtons.cancel(),
                                    onPrimaryButtonClick: ()=>dismissModal(true),
                                    onSecondaryButtonClick: ()=>dismissModal(false)
                                });
                            }
                        });
                        if (!confirmed) return;
                        draftProposal({
                            relationshipId: proposal.id,
                            draftId: proposal.draftId
                        });
                    }
                }
            };
        },
        getSendProposalReminderAction (proposalId, isSampleProposal) {
            return {
                text: texts.sendReminder(),
                intent: Intent.SendProposalReminder,
                disabled: isSampleProposal,
                onClick: ()=>{
                    showModal({
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(RestfulWrapper, {
                                fetch: ()=>api.relationship.get(proposalId),
                                renderChild: (param)=>{
                                    let { data } = param;
                                    return /*#__PURE__*/ _jsx(MissingSignaturesReminderModal, {
                                        dismissModal: dismissModal,
                                        relationship: data
                                    });
                                }
                            });
                        }
                    });
                }
            };
        },
        getDuplicateProposalAction (proposalId, param) {
            let { isSampleProposal, draftId } = param;
            return {
                text: texts.duplicate(),
                intent: Intent.DuplicateProposal,
                disabled: isSampleProposal,
                onClick: ()=>{
                    duplicateProposal({
                        relationshipId: proposalId,
                        draftId
                    });
                }
            };
        },
        getCopyProposalLinkAction (proposal) {
            return {
                text: texts.copyLink(),
                intent: Intent.CopyRelationshipDirectLink,
                disabled: false,
                onClick: ()=>{
                    reportToGtm('button-click', 'vendor-copy-link-proposal', {
                        progressStatus: proposal.progressStatus
                    });
                    reportToAppcues('button-click', 'vendor-copy-link-proposal');
                    copyAgreementLink({
                        agreementId: proposal.id,
                        vendorId: proposal.vendor.id,
                        customerId: proposal.customer.id,
                        progressStatus: proposal.progressStatus,
                        withGlobalLoader: true
                    });
                }
            };
        },
        getCancelProposalAction (proposalId, onSuccess) {
            return {
                text: texts.withdraw(),
                disabled: false,
                intent: Intent.CancelProposal,
                onClick: ()=>cancelProposalById(proposalId, onSuccess)
            };
        }
    };
}
